import type { PublicCatalogBrand } from "@workspace/api-client-react";

export function getHomepageSectionBrands(
  catalogBrands: readonly PublicCatalogBrand[],
  easternProductCount: number,
): PublicCatalogBrand[] {
  const brands = new Map(
    catalogBrands
      .filter(brand => brand.slug !== "ethnc")
      .map(brand => [brand.slug, brand] as const),
  );

  brands.set("ethnc", {
    slug: "ethnc",
    name: "Ethnc",
    homepageVisible: true,
    homepageOrder: -1,
    featured: true,
    categories: [],
    productCount: Math.max(0, easternProductCount),
  });

  return [...brands.values()].sort(
    (left, right) => left.homepageOrder - right.homepageOrder || left.name.localeCompare(right.name),
  );
}

export function getHomepageShopByBrandBrands(
  brands: readonly PublicCatalogBrand[],
): PublicCatalogBrand[] {
  return brands.filter(brand => brand.productCount > 0);
}

export function getHomepageVisibleBrands(
  brands: readonly PublicCatalogBrand[],
): PublicCatalogBrand[] {
  return brands.filter(brand =>
    brand.productCount > 0 && (brand.slug === "ethnc" || brand.homepageVisible),
  );
}

export function getHomepageSectionEligibilityWarning({
  kind,
  brandName,
  brandProductCount,
  brandInPublicCatalog = true,
  saleProductCount,
  saleEnabled = true,
  selectedHandleCount,
  selectedHandleTarget = 0,
}: {
  kind: "brand-feature" | "product-collection" | "sale";
  brandName?: string;
  brandProductCount?: number | null;
  brandInPublicCatalog?: boolean;
  saleProductCount?: number | null;
  saleEnabled?: boolean;
  selectedHandleCount?: number | null;
  selectedHandleTarget?: number;
}): string | null {
  const label = brandName?.trim() || "This section";

  if (kind === "sale") {
    if (!saleEnabled) {
      return "Sale sections are disabled in storefront settings, so this section will not render.";
    }
    const count = selectedHandleTarget > 0 ? selectedHandleCount : saleProductCount;
    if (count == null) return null;
    if (count < 4) {
      return `${label} has ${count} eligible sale products for this layout; at least 4 are required for the Sale section to render.`;
    }
    return null;
  }

  if (selectedHandleTarget > 0 && selectedHandleCount != null) {
    if (selectedHandleCount === 0) {
      return `${label} has 0 eligible products for this layout because none of the selected handles currently match the public product list.`;
    }
    if (selectedHandleCount < selectedHandleTarget) {
      return `${label} has ${selectedHandleCount} of ${selectedHandleTarget} selected products eligible for this layout. Replace or remove the unavailable handles.`;
    }
  }

  if (brandProductCount == null || brandProductCount > 0) return null;
  if (!brandInPublicCatalog) {
    return `${label} has 0 eligible products for this layout because it is not currently in the public catalog list. Check its existing approval and product visibility; no settings were changed.`;
  }
  return `${label} has 0 eligible products for this layout. The public count excludes inactive or Needs review items and, when sold-out items are hidden, variants without stock.`;
}
